import { useEffect, useState } from "react";
import { CRM_PRIVACY_EVENT, setCrmPrivacyChoice } from "@/services/crmActivity";

type AnalyticsPreference = "analytics" | "essential" | null;

const STORAGE_KEY = "umasaura_analytics_preference_v1";
const TAG_MANAGER_ID = "GTM-TZDRVN87";
// Google Analytics 4 (owner request 04-Nov-2026). Loaded only after the visitor opts in, like GTM.
const GA4_MEASUREMENT_ID = "G-GL5YKGC21T";

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
    gtag?: (...args: unknown[]) => void;
  }
}

let tagManagerStarted = false;

const readPreference = (): AnalyticsPreference => {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value === "analytics" || value === "essential" ? value : null;
  } catch {
    return null;
  }
};

const startTagManager = () => {
  if (tagManagerStarted || document.querySelector('script[data-analytics-tag-manager="true"]')) return;
  tagManagerStarted = true;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${TAG_MANAGER_ID}`;
  script.dataset.analyticsTagManager = "true";
  document.head.appendChild(script);
  startGoogleAnalytics();
};

const startGoogleAnalytics = () => {
  if (document.querySelector('script[data-analytics-ga4="true"]')) return;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // gtag.js expects the raw arguments object on the data layer.
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments as unknown as Record<string, unknown>);
  };
  window.gtag("js", new Date());
  window.gtag("config", GA4_MEASUREMENT_ID);
  const ga = document.createElement("script");
  ga.async = true;
  ga.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
  ga.dataset.analyticsGa4 = "true";
  document.head.appendChild(ga);
};

const clearFirstPartyAnalyticsCookies = () => {
  for (const cookie of document.cookie.split(";")) {
    const name = cookie.trim().split("=")[0];
    if (!/^(?:_ga(?:_.+)?|_gid|_fbp|_fbc|_gcl_.+|_clck|_clsk)$/.test(name)) continue;
    document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax`;
    document.cookie = `${name}=; Max-Age=0; Path=/; Domain=.umasaura.com; SameSite=Lax`;
  }
};

export function AnalyticsChoice() {
  const [preference, setPreference] = useState<AnalyticsPreference>(null);
  const [preferenceReady, setPreferenceReady] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    setPreference(readPreference());
    setPreferenceReady(true);
  }, []);

  useEffect(() => {
    if (preference !== "analytics") return;
    if ("requestIdleCallback" in window) {
      const id = window.requestIdleCallback(startTagManager, { timeout: 5000 });
      return () => window.cancelIdleCallback(id);
    }
    const id = globalThis.setTimeout(startTagManager, 1000);
    return () => globalThis.clearTimeout(id);
  }, [preference]);

  const choose = (next: Exclude<AnalyticsPreference, null>) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // The choice still applies for this page if browser storage is unavailable.
    }
    if (next === "essential") clearFirstPartyAnalyticsCookies();
    const hadAnalytics = preference === "analytics";
    setPreference(next);
    setCrmPrivacyChoice(next);
    window.dispatchEvent(new Event(CRM_PRIVACY_EVENT));
    setSettingsOpen(false);
    if (next === "analytics") startTagManager();
    if (next === "essential" && hadAnalytics) window.location.reload();
  };

  if (!preferenceReady || (preference !== null && !settingsOpen)) {
    return (
      <button type="button" onClick={() => setSettingsOpen(true)}
        className="fixed bottom-3 left-3 z-[70] rounded-full border border-amber-900/30 bg-white px-3 py-2 text-xs font-semibold text-amber-950 shadow-md hover:bg-amber-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-800">
        Privacy choices
      </button>
    );
  }

  return (
    <section aria-labelledby="analytics-choice-title"
      className="fixed inset-x-3 bottom-3 z-[70] mx-auto max-w-lg rounded-xl border border-amber-900/20 bg-white p-4 text-stone-900 shadow-2xl sm:inset-x-auto sm:left-4 sm:mx-0 sm:p-5">
      <h2 id="analytics-choice-title" className="text-base font-bold">Your privacy choices</h2>
      <p className="mt-2 text-sm leading-5 text-stone-700">
        Optional analytics help us understand visits and improve the store. Shopping, cart and checkout work with essential features only.
      </p>
      <p className="mt-2 text-xs text-stone-600">
        Read our <a href="/privacy-policy" className="font-semibold underline underline-offset-2 hover:text-amber-900">Privacy Policy</a>. You can change this choice later.
      </p>
      <div className="mt-4 flex flex-wrap gap-2">
        <button type="button" onClick={() => choose("essential")}
          className="min-h-11 flex-1 rounded-lg border border-stone-400 bg-white px-3 py-2 text-sm font-semibold text-stone-900 hover:bg-stone-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-800">
          Essential only
        </button>
        <button type="button" onClick={() => choose("analytics")}
          className="min-h-11 flex-1 rounded-lg bg-amber-900 px-3 py-2 text-sm font-semibold text-white hover:bg-amber-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-800 focus-visible:ring-offset-2">
          Accept analytics
        </button>
      </div>
    </section>
  );
}
